import { MoreHoriz } from '@mui/icons-material'
import { Box, Button, IconButton, Typography } from '@mui/material'
import type { Block, EmbedReference, InlineContent, ListItem, TextNode } from '@shared/ast/astTypes'
import { type NodeKey } from 'lexical'
import React, { createContext, useContext, useState } from 'react'

import { useEmbedActions } from './EmbedActionsMenu'
import { useSourceEmbed } from './useSourceEmbed'
import { type ResolvedEmbed } from '../../../services/api'
import { resolveImageUrl } from '../../../utils/imageUrl'

export const EmbedContext = createContext<{
  bookId?: string
  isAuthor: boolean
  onOpenSource?: (filename: string, blockId: string) => void
}>({ isAuthor: false })

function formattedText(node: TextNode, key: number): React.ReactNode {
  const style: React.CSSProperties = {}
  for (const mark of node.marks || []) {
    if (mark === 'bold') {
      style.fontWeight = 'bold'
    } else if (mark === 'italic') {
      style.fontStyle = 'italic'
    } else if (mark === 'underline') {
      style.textDecoration = 'underline'
    } else if (typeof mark === 'object') {
      if (mark.type === 'font') {
        style.fontFamily = mark.family
      } else if (mark.type === 'fontSize') {
        style.fontSize = mark.value
      } else if (mark.type === 'color') {
        style.color = mark.value
      }
    }
  }
  return (
    <span key={key} style={style}>
      {node.text}
    </span>
  )
}

export function renderInlineEmbedContent(content: InlineContent[]): React.ReactNode {
  return content.map((node, index) => {
    if (node.type === 'text') {
      return formattedText(node, index)
    }
    if (node.type === 'link') {
      const safeUrl = /^(https?:|mailto:|\/|#)/i.test(node.url) ? node.url : undefined
      return (
        <a key={index} href={safeUrl} rel="noopener noreferrer">
          {node.content.map(formattedText)}
        </a>
      )
    }
    if (node.type === 'inline-embed') {
      return <span key={index}>המקור המשובץ אינו זמין</span>
    }
    // The resolver applies the source's access rules. For readers this is already
    // ciphertext (or permitted plaintext); never fetch author content here.
    return (
      <span key={index} style={{ whiteSpace: 'pre-wrap' }}>
        {node.content.map(formattedText)}
      </span>
    )
  })
}

function listItems(items: ListItem[]): React.ReactNode {
  return items.map((item, index) => (
    <li key={index} dir={item.attrs?.dir}>
      {renderInlineEmbedContent(item.content)}
      {item.children?.length
        ? React.createElement(item.childrenOrdered ? 'ol' : 'ul', {}, listItems(item.children))
        : null}
    </li>
  ))
}

function renderBlock(block: Block, index: number): React.ReactNode {
  const props = { key: index, dir: 'dir' in (block.attrs || {}) ? (block.attrs as { dir?: string }).dir : undefined }
  switch (block.type) {
    case 'heading':
      return React.createElement(`h${block.level}`, props, renderInlineEmbedContent(block.content))
    case 'paragraph':
      return <p {...props}>{renderInlineEmbedContent(block.content)}</p>
    case 'quote':
      return <blockquote {...props}>{renderInlineEmbedContent(block.content)}</blockquote>
    case 'list':
      return React.createElement(block.ordered ? 'ol' : 'ul', props, listItems(block.items))
    case 'divider':
      return <hr key={index} />
    case 'image':
      return (
        <figure key={index}>
          <img src={resolveImageUrl(block.url)} alt={block.alt || ''} style={{ maxWidth: '100%' }} />
          {block.caption && <figcaption>{renderInlineEmbedContent(block.caption)}</figcaption>}
        </figure>
      )
    case 'embed':
      return <p key={index}>המקור המשובץ אינו זמין</p>
  }
}

export const embedStatusText: Record<Exclude<ResolvedEmbed['status'], 'resolved'>, string> = {
  missing: 'המקור אינו זמין או שנמחק',
  ambiguous: 'נמצאו כמה מקורות תואמים — יש לבחור את המקור מחדש',
  cycle: 'לא ניתן להציג שיבוץ שמפנה לעצמו',
}

export function SourceEmbedView({ source, nodeKey }: { source: EmbedReference; nodeKey: NodeKey }) {
  const context = useContext(EmbedContext)
  const { bookId, isAuthor } = context
  const { result, failed } = useSourceEmbed(bookId, source)
  const actions = useEmbedActions({ ...context, source, nodeKey, result, failed, presentation: 'card' })
  const [expanded, setExpanded] = useState(false)

  const long =
    (result?.content?.blocks.length || 0) > 4 ||
    (result?.content ? JSON.stringify(result.content).length > 1800 : false)
  return (
    <>
      <Box
        contentEditable={false}
        data-embed-status={failed ? 'error' : result?.status || 'loading'}
        sx={{
          my: 2,
          borderInlineStart: '3px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
          px: 2,
          py: 1.5,
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Typography component="span" variant="caption" color="text.secondary">
            {result?.title || source.filename} · טקסט משובץ
          </Typography>
          {isAuthor && (
            <IconButton
              size="small"
              aria-label="פעולות טקסט משובץ"
              title="פעולות טקסט משובץ"
              aria-haspopup="menu"
              aria-expanded={actions.expanded}
              sx={{ marginInlineStart: 'auto' }}
              onClick={actions.open}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
          )}
        </Box>
        {failed || !bookId ? (
          <Typography color="text.secondary">לא ניתן לטעון את המקור כעת</Typography>
        ) : !result ? (
          <Typography color="text.secondary">טוען את המקור…</Typography>
        ) : result.status !== 'resolved' ? (
          <Typography color="text.secondary">{embedStatusText[result.status]}</Typography>
        ) : (
          <>
            <Box
              dir={result.content?.meta.dir}
              sx={{
                whiteSpace: 'pre-wrap',
                maxHeight: long && !expanded ? 260 : undefined,
                overflow: 'hidden',
                '& > :first-of-type': { mt: 0 },
                '& > :last-child': { mb: 0 },
                '& p': { my: 1 },
                '& h1, & h2, & h3, & h4, & h5, & h6': { fontSize: '1.1em', my: 1.5 },
              }}
            >
              {result.content?.blocks.map(renderBlock)}
            </Box>
            {long && (
              <Button size="small" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>
                {expanded ? 'קפל' : 'הצג הכול'}
              </Button>
            )}
          </>
        )}
      </Box>
      {actions.menu}
    </>
  )
}
